import React, { useRef, useState } from 'react';

import Modal from 'react-modal';
import AsyncSelect from 'react-select/async';

/** components */
import LabelWithTooltip from './LabelWithTooltip';
import SafeHtml from './SafeHtml';

/** images */
import { formatOptionDescription, formatOptionName } from '../../helpers/format';

/** service */
import { getNAICSCode } from '../../service/api';

const NaicsCode = ({
  data,
  setData,
  dataError,
  setDataError,
  field,
  toggleFieldModal,
  isModalOpen,
  fieldClassName,
  handleChange,
}) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [naicsCodeTitles, setNaicsCodeTitles] = useState([]);
  const [selectedValue, setSelectedValue] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [highlightValue, setHighLightValue] = useState('');
  const [naicsCodeDescriptions, setNaicsCodeDescriptions] = useState([]);
  const [selectedCodeDescription, setSelectedCodeDescription] = useState(null);
  const timerRef = useRef(null);

  const handleSave = () => {
    setData({
      ...data,
      [field.name]: selectedValue.code,
    });
    setDataError((dataError) => ({
      ...dataError,
      [field.name]: '',
    }));
    setShowSearchModal(false);
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: '#727272',
      fontFamily: "'Lato', sans-serif !important",
      fontWeight: 300,
      fontSize: 19,
      padding: 10,
      backgroundColor: state.isFocused ? '#E0EBFD' : '#fff',
    }),
  };

  const handleSelectCodeDescription = (event) => {
    setSelectedCodeDescription(event.target.value);
  };
  const formatOptionLabel = (option) => {
    if (!option || !option.code || !option.title) {
      return (
        <SafeHtml
          originalHtml={formatOptionName('', highlightValue)}
          className="selected-address"
        />
      );
    }
    const showValue = `${option.code} - ${option.title}`;
    const filteredDescription = option.descriptions
      .filter((text) => text.toLowerCase().includes(searchValue.toLowerCase()))
      .slice(0, 2);
    const renderDescription = () => (
      <div className="selected-description-wrapper">
        {filteredDescription.map((text, index) => {
          return (
            <SafeHtml
              key={text + index}
              originalHtml={formatOptionDescription(text, highlightValue)}
              className="selected-description"
            />
          );
        })}
      </div>
    );
    return (
      <>
        <SafeHtml
          originalHtml={formatOptionName(showValue, highlightValue)}
          className="selected-address"
        />
        {!selectedValue.code && renderDescription()}
      </>
    );
  };

  const handleOnInputChange = (event) => {
    if (!event) {
      return;
    }
    if (selectedValue.code) {
      setSelectedValue({});
    }
    setHighLightValue(event);
    setSearchValue(event);
  };

  const handleChangeCode = (e) => {
    if (!e) {
      setSelectedValue({});
      setSelectedCodeDescription(null);
      setNaicsCodeDescriptions([]);
      return;
    }
    const findValue = naicsCodeTitles.filter((el) => {
      return el.code === e.code;
    });
    if (!findValue.length) {
      return;
    }
    setSelectedValue(findValue[0]);
    if (findValue[0].descriptions && findValue[0].descriptions.length > 0) {
      const filteredDescription = findValue[0].descriptions.filter((text) =>
        text.toLowerCase().includes(searchValue.toLowerCase())
      );
      setNaicsCodeDescriptions(filteredDescription);
      if (filteredDescription.length === 1) {
        setSelectedCodeDescription(filteredDescription[0]);
      }
    }
    setHighLightValue('');
  };

  const getNAICSCodeWithDelay = (str) => {
    clearTimeout(timerRef.current);
    return new Promise((resolve) => {
      timerRef.current = setTimeout(async () => {
        const resp = await getNAICSCode(str);
        if (resp.status === 200) {
          const {
            data: {
              hits: { hits },
            },
          } = resp;
          const CodeTitles = hits.map((naicsCode) => {
            return {
              code: naicsCode._source.code,
              title: naicsCode._source.title,
              descriptions: naicsCode._source.descriptions,
            };
          });
          setNaicsCodeTitles(CodeTitles);
          resolve(CodeTitles);
        }
      }, 200);
    });
  };

  const loadOptions = async (e) => {
    if (!e) {
      return;
    }
    return await getNAICSCodeWithDelay(e);
  };

  const handleNoResults = () => null;

  const handleClose = () => {
    setShowSearchModal(false);
  };

  return (
    <>
      <div key={field.name} className={fieldClassName}>
        <div className="form-field">
          <LabelWithTooltip
            field={field}
            toggleFieldModal={toggleFieldModal}
            isModalOpen={isModalOpen}
          />
          <input
            id={field.name}
            value={data[field.name]}
            name={field.name}
            onChange={(e) => {
              if (e.target.value.length > 6) {
                return false;
              }
              handleChange(e, field.name, field);
            }}
            type={field.type}
            className={dataError[field.name] ? 'error' : ''}
            spellCheck={false}
          />
          <div className="naics-code-link" onClick={() => setShowSearchModal(true)}>
            Search for Your NAICS Code
          </div>
        </div>
        {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
      </div>

      <Modal isOpen={showSearchModal} className="field-modal naics-code-search">
        <span className="close" onClick={handleClose}>
          x
        </span>
        <div className="modal-header">
          <h3>NAICS Code Search</h3>
        </div>
        <div className="modal-content">
          <div className="horizon-form-field">
            <div className="horizon-form-field-inner">
              <label htmlFor="Products / Services Sold">Products / Services Sold:</label>
              <div className="search-input">
                <AsyncSelect
                  defaultOptions
                  type="text"
                  id={field.name}
                  name={field.name}
                  formatOptionLabel={formatOptionLabel}
                  value={selectedValue}
                  getOptionValue={(e) => e.code}
                  onChange={handleChangeCode}
                  onInputChange={handleOnInputChange}
                  classNamePrefix="react-select"
                  styles={customStyles}
                  loadOptions={loadOptions}
                  noOptionsMessage={handleNoResults}
                  isClearable
                />
              </div>
            </div>
            <div className="horizon-form-field">
              <div className="horizon-form-field-inner">
                <label htmlFor="">NAICS Index Entries:</label>
                <select
                  name="CodeDescriptions"
                  id="CodeDescriptions"
                  onChange={handleSelectCodeDescription}
                  value={selectedValue.code ? selectedCodeDescription : ''}
                >
                  <option value="">Select a NAICS index classification</option>
                  {naicsCodeDescriptions &&
                    naicsCodeDescriptions.length > 0 &&
                    naicsCodeDescriptions.map((codeDescription, index) => (
                      <option value={codeDescription} key={codeDescription + '-' + index}>
                        {codeDescription}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
          <div className="naics-footer">
            <div className="naics-help">
              Unable to find your NAICS Code? Please visit the{' '}
              <a href="https://www.census.gov/naics/" target="_blank" rel="noreferrer">
                U.S. Census Bureau
              </a>{' '}
              for other NAICS Code Search options. Once you find your NAICS Code, exit this NAICS
              Code Search window and type in your 6-digit NAICS Code directly into the NAICS Code
              field.
            </div>
            <div className="horizon-form-field">
              <div className="horizon-form-field-inner">
                <label htmlFor="empty-space" />
                <div className="action-groups">
                  <button type="button" onClick={handleSave}>
                    Save & Exit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default NaicsCode;
